import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ProductType } from 'types/product.types';

export type MyShopPageReducer = {
  availableProducts: ProductType[];
  availableTotal: number | null;
  otherStatusesProducts: ProductType[];
  otherTotal: number | null;
  deletedProductIds: string[];
};

const initialState: MyShopPageReducer = {
  availableProducts: [],
  availableTotal: null,
  otherStatusesProducts: [],
  otherTotal: null,
  deletedProductIds: [],
};

const myShopPageSlice = createSlice({
  name: 'myShopPage',
  initialState,
  reducers: {
    setAvailableProducts(state, action: PayloadAction<{ products: ProductType[]; total: number }>) {
      const { products, total } = action.payload;
      const otherIds = new Set(state.otherStatusesProducts.map((p) => p.id));

      products.forEach((product) => {
        if (!otherIds.has(product.id) && !state.availableProducts.some((p) => p.id === product.id)) {
          state.availableProducts.push(product);
        }
      });
      state.availableTotal = total;
    },

    setOtherProducts(state, action: PayloadAction<{ products: ProductType[]; total: number }>) {
      const { products, total } = action.payload;
      const availableIds = new Set(state.availableProducts.map((p) => p.id));

      products.forEach((product) => {
        if (!availableIds.has(product.id) && !state.otherStatusesProducts.some((p) => p.id === product.id)) {
          state.otherStatusesProducts.push(product);
        }
      });
      state.otherTotal = total;
    },

    removeProduct(state, action: PayloadAction<{ productId: string }>) {
      const { productId } = action.payload;
      const availableIndex = state.availableProducts.findIndex((p) => String(p.id) === productId);
      const otherIndex = state.otherStatusesProducts.findIndex((p) => String(p.id) === productId);

      if (availableIndex !== -1) {
        state.availableProducts.splice(availableIndex, 1);
        if (state.availableTotal !== null) {
          state.availableTotal -= 1;
        }
      }

      if (otherIndex !== -1) {
        state.otherStatusesProducts.splice(otherIndex, 1);
        if (state.otherTotal !== null) {
          state.otherTotal -= 1;
        }
      }
    },

    updateProduct(state, action: PayloadAction<{ product: ProductType }>) {
      const { product } = action.payload;
      const availableIndex = state.availableProducts.findIndex((p) => p.id === product.id);
      const otherIndex = state.otherStatusesProducts.findIndex((p) => p.id === product.id);

      if (availableIndex !== -1) {
        state.availableProducts[availableIndex] = product;
      }

      if (otherIndex !== -1) {
        state.otherStatusesProducts[otherIndex] = product;
      }
    },

    addProduct(state, action: PayloadAction<{ product: ProductType }>) {
      const { product } = action.payload;

      state.availableProducts.unshift(product);
      state.availableTotal = state.availableTotal !== null ? state.availableTotal + 1 : 1;
    },

    moveProductToOtherStatuses(state, action: PayloadAction<{ product: ProductType }>) {
      const { product } = action.payload;
      const availableIndex = state.availableProducts.findIndex((p) => p.id === product.id);

      if (availableIndex !== -1) {
        state.availableProducts.splice(availableIndex, 1);
        if (state.availableTotal !== null) {
          state.availableTotal -= 1;
        }
      }

      state.otherStatusesProducts.unshift(product);
      state.otherTotal = state.otherTotal !== null ? state.otherTotal + 1 : 1;
    },

    moveProductToAvailable(state, action: PayloadAction<{ product: ProductType }>) {
      const { product } = action.payload;
      const otherIndex = state.otherStatusesProducts.findIndex((p) => p.id === product.id);

      if (otherIndex !== -1) {
        state.otherStatusesProducts.splice(otherIndex, 1);
        if (state.otherTotal !== null) {
          state.otherTotal -= 1;
        }
      }

      state.availableProducts.unshift(product);
      state.availableTotal = state.availableTotal !== null ? state.availableTotal + 1 : 1;
    },

    markProductDeleted(state, action: PayloadAction<{ productId: string }>) {
      state.deletedProductIds.push(action.payload.productId);
    },

    reset() {
      return initialState;
    },
  },
});

export const myShopPageActions = myShopPageSlice.actions;

export default myShopPageSlice.reducer;
